import {
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from "@angular/core"
import { ActivatedRoute, Params } from "@angular/router"
import { CHNode } from "../../models/node.model"
import { BrowsingService } from "../../services/browsing.service"
import { ColorCodeService } from "../../services/color-code.service"
import { GTMService } from "../../services/gtm.service"
import { MediaTypeNavService } from "../../services/media-type-navigation.service"
import { MediaTypeNavComponent } from "../../shared-components/media-type-nav/media-type-nav.component"
import { CommonBrowseResultsGroup } from "./common-browse-results-group"
import Utils from "../../utils/utils"
import { inViewport } from "../../app.config"

@Component({
    selector: "app-browsing-results-group",
    templateUrl: "./browsing-results-group.component.html",
    styleUrls: ["./browsing-results-group.component.scss"],
})
export class BrowsingResultsGroupComponent
    extends CommonBrowseResultsGroup
    implements OnInit, OnDestroy {
    public activeNode: CHNode
    public colorCode = "#fff"
    public bootstrapClasses: string = ""
    public latestRequestedQueryParams: Params = null
    public latestRequestedSlug: string = null
    public gtmPrefix: string = "gtm-results-browse-card"
    public innerWidth = window.innerWidth
    public cardTypeChangeBreakpoint: number = 710
    public inViewportConfig = inViewport

    @Input() public resultsPerRow: number = 3 //default

    @ViewChild("categoryNavigation")
    public categoryNavigation: MediaTypeNavComponent
    @ViewChildren("categoryTitle") public categoryTitles: QueryList<ElementRef>

    constructor(
        browsingService: BrowsingService,
        private mediaTypeNavService: MediaTypeNavService,
        private route: ActivatedRoute,
        private colorCodeService: ColorCodeService,
        private gtmService: GTMService
    ) {
        super(browsingService)
        this.colorCode = this.colorCodeService.getPrimaryColor()
    }

    public ngOnInit() {
        this.innerWidth = window.innerWidth
        Utils.resize(() => {
            this.innerWidth = window.innerWidth
        })
        this.getResults()
        this.subscriptions.push(
            this.route.queryParams.subscribe((queryParams: Params) => {
                if (
                    !!this.latestRequestedQueryParams &&
                    JSON.stringify(this.latestRequestedQueryParams) !== JSON.stringify(queryParams)
                ) {
                    // When filters change, reselect last node to force results reload
                    this.browsingService.emitLastNodeSection()
                }
            })
        )

        this.subscriptions.push(
            this.browsingService.activeNodeChanged.subscribe((node: CHNode) => {
                if (!node.isLoaded()) {
                    return
                }
                const currentRouteSlug = CHNode.cleanSlug(this.route.snapshot.params.nodeSlug)
                if (currentRouteSlug !== node.slug) {
                    // Ignore deprecated event
                    return
                }

                const currentParams = this.route.snapshot.queryParams
                const isSameNode = !!this.activeNode && node.id === this.activeNode.id
                if (
                    isSameNode &&
                    JSON.stringify(currentParams) ===
                        JSON.stringify(this.latestRequestedQueryParams)
                ) {
                    // Ignore duplicate event
                    return
                }
                this.gtmService.resetDataLayer()
                this.gtmService.pushPageView()
                this.activeNode = node
                this.colorCode = this.colorCodeService.getColorForNode(node)
                // Reset selected anchor
                this.mediaTypeNavService.categoryTitleInViewport.emit("")
                this.getResults(node)
            })
        )
    }

    public getResults(node: CHNode = null) {
        let nodeIdOrSlug
        const currentParams = this.route.snapshot.queryParams
        if (node) {
            if (
                node.slug == this.latestRequestedSlug &&
                JSON.stringify(this.latestRequestedQueryParams) == JSON.stringify(currentParams)
            ) {
                // Already requested/loaded
                return
            }
            nodeIdOrSlug = node.id
            this.latestRequestedSlug = node.slug
        } else {
            // Workaround to load results faster by slug (the first time) and then by node id
            nodeIdOrSlug = CHNode.cleanSlug(this.route.snapshot.params.nodeSlug)
            this.latestRequestedSlug = nodeIdOrSlug
        }
        this.customSubscriptions.map((subscription) => subscription.unsubscribe())
        this.clearSearchData()
        this.latestRequestedQueryParams = currentParams

        this.resultsObs = this.browsingService.getResults(
            nodeIdOrSlug,
            this.latestRequestedQueryParams
        )

        if (this.resultsObs.length) {
            this.customSubscriptions.push(
                this.resultsObs[0].subscribe((data: any) => {
                    this.processResults(data, 0)
                })
            )
        }
    }

    public hasFilter() {
        return Object.keys(this.latestRequestedQueryParams).length > 0
    }
}
